import { useState, useEffect, useRef, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/lib/auth';
import { Button } from './Button';

interface LoginModalProps {
  onClose: () => void;
}

type Step = 'email' | 'otp';

export function LoginModal({ onClose }: LoginModalProps) {
  const { signInWithOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'email') emailRef.current?.focus();
    else otpRef.current?.focus();
  }, [step]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSendOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setIsLoading(true);
    try {
      await signInWithOtp(email.trim());
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setError(null);
    setIsLoading(true);
    try {
      await verifyOtp(email.trim(), otp.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code');
    } finally {
      setIsLoading(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div className="relative w-full max-w-sm overflow-hidden border border-rule bg-panel shadow-2xl">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 p-1.5 text-dim transition-colors hover:bg-bg hover:text-ink"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="px-7 pb-7 pt-8">
          {/* Header — brand logo, no gradient */}
          <div className="mb-6 flex flex-col items-center text-center">
            <span className="flex h-9 w-9 items-center justify-center bg-accent text-lg font-bold text-bg">
              ≡
            </span>
            <h2 className="mt-3 text-lg font-semibold tracking-tight text-ink">
              {step === 'email' ? (
                <>
                  Sign in to llmcontext<span className="text-accent">.dev</span>
                </>
              ) : (
                'Check your email'
              )}
            </h2>
            <p className="mt-1 text-sm text-dim">
              {step === 'email' ? (
                "Enter your email — we'll send a one-time code."
              ) : (
                <>
                  We sent a code to <span className="font-medium text-ink">{email}</span>
                </>
              )}
            </p>
          </div>

          {step === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-3">
              <div>
                <label htmlFor="login-email" className="mb-1.5 block text-xs font-medium text-dim">
                  Email address
                </label>
                <input
                  ref={emailRef}
                  id="login-email"
                  className="lc-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 border border-del/30 bg-del/10 px-3 py-2">
                  <svg
                    className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-del"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-xs text-del">{error}</p>
                </div>
              )}
              <Button type="submit" className="w-full justify-center" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Sending code…
                  </span>
                ) : (
                  'Send one-time code'
                )}
              </Button>
              <p className="pt-1 text-center text-2xs text-dim">
                No password — we'll email you a 6-digit code.
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-3">
              <div>
                <label htmlFor="login-otp" className="mb-1.5 block text-xs font-medium text-dim">
                  Verification code
                </label>
                <input
                  ref={otpRef}
                  id="login-otp"
                  className="lc-input text-center font-mono text-lg tracking-[0.3em]"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  required
                  autoComplete="one-time-code"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 border border-del/30 bg-del/10 px-3 py-2">
                  <svg
                    className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-del"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-xs text-del">{error}</p>
                </div>
              )}
              <Button
                type="submit"
                className="w-full justify-center"
                disabled={isLoading || otp.length < 6}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Verifying…
                  </span>
                ) : (
                  'Sign in'
                )}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                  setError(null);
                }}
                className="block w-full pt-1 text-center text-xs text-dim transition-colors hover:text-ink"
              >
                ← Use a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
